import React from 'react';
import { Link } from 'react-router-dom';
import { HubMarketingNav } from '../../components/ui/HubHeader';
import Seo from '../../components/Seo';

const SheetsGoogleReviewPage = () => {
  const marketplaceUrl = (import.meta.env.VITE_SHEETS_MARKETPLACE_URL || '').trim()
    || 'https://workspace.google.com/marketplace/search/Neevii%20Smart%20Expenses';

  const desktopVideo = '/assets/videos/Google_requirements.mp4';
  const mobileVideo = '/assets/videos/Google_requirements_mobile.mp4';
  const poster = '/assets/videos/Google_requirements_poster.jpg';

  return (
    <>
      <Seo
        title="Google Marketplace Review Demo | Neevii Smart Expenses"
        description="Review demo for Neevii Smart Expenses Google Sheets add-on covering OAuth consent, CSV handling, and receipt-to-category workflow."
      />
      <div className="min-h-screen text-foreground">
        <HubMarketingNav preset="sheets" />
        <main className="pt-10 pb-16 px-6 md:pt-12 lg:px-8">
          <div className="mx-auto max-w-5xl space-y-8">
            <section className="rounded-3xl border border-border bg-card/40 p-6 lg:p-8 space-y-4">
              <span className="inline-flex rounded-full bg-[#065aaf] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                Google Marketplace Review
              </span>
              <h1 className="text-3xl md:text-4xl font-semibold leading-tight">
                Neevii Smart Expenses OAuth, Consent, CSV, and Receipt Flow
              </h1>
              <p className="text-muted-foreground max-w-3xl">
                Dedicated review page for Google verification. This demo shows the full user journey:
                OAuth consent, sidebar authorization, receipt capture, category mapping, and CSV-ready output.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={marketplaceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-lg bg-brand px-5 py-3 text-sm font-semibold uppercase tracking-wide text-slate-900 hover:bg-brand/90"
                >
                  Open Marketplace Listing
                </a>
                <Link
                  to="/sheets"
                  className="inline-flex items-center justify-center rounded-lg border border-[#065aaf] px-5 py-3 text-sm font-semibold uppercase tracking-wide text-[#065aaf] hover:bg-[#065aaf]/10"
                >
                  Back to Sheets page
                </Link>
              </div>
            </section>

            <section className="rounded-2xl border border-border bg-card/30 p-4 lg:p-6 space-y-4">
              <h2 className="text-2xl font-semibold">Verification Demo Video</h2>
              <p className="text-sm text-muted-foreground">
                Includes Google auth and consent behavior, CSV handling, and receipt processing in one continuous flow.
              </p>
              <div className="overflow-hidden rounded-xl border border-border/60 bg-card/40">
                <video
                  className="h-auto w-full"
                  controls
                  preload="metadata"
                  poster={poster}
                  playsInline
                >
                  <source src={mobileVideo} type="video/mp4" media="(max-width: 768px)" />
                  <source src={desktopVideo} type="video/mp4" />
                </video>
              </div>
            </section>

            <section className="grid gap-4 md:grid-cols-3">
              <article className="rounded-2xl border border-border bg-card/30 p-5">
                <h3 className="text-lg font-semibold mb-2">OAuth Consent</h3>
                <p className="text-sm text-muted-foreground">
                  Demonstrates the Google sign-in and consent steps for the Sheets add-on.
                </p>
              </article>
              <article className="rounded-2xl border border-border bg-card/30 p-5">
                <h3 className="text-lg font-semibold mb-2">Receipt Mapping</h3>
                <p className="text-sm text-muted-foreground">
                  Shows receipt extraction and category mapping into tax-ready rows.
                </p>
              </article>
              <article className="rounded-2xl border border-border bg-card/30 p-5">
                <h3 className="text-lg font-semibold mb-2">CSV/Sheet Output</h3>
                <p className="text-sm text-muted-foreground">
                  Confirms resulting data structure for spreadsheet and CSV workflows.
                </p>
              </article>
            </section>

            <section className="rounded-2xl border border-brand/30 bg-brand/10 p-5">
              <h2 className="text-lg font-semibold">OAuth Branding Note</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Consent dialog branding is controlled by the linked Google Cloud project OAuth consent screen,
                not the Apps Script project title. Verify branding in the exact linked GCP project.
              </p>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default SheetsGoogleReviewPage;
